import { Box, Center, Container, Flex, Grid, GridItem, Heading, Icon, Portal, Skeleton, Text, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import { LuCalendarClock } from 'react-icons/lu';
import { formattedDate3 } from '../../../helper/utils/formattedDate';
import useOnlineSales from '../hooks/useOnlineSales';

function startOfDay(d) {
    const x = d ? new Date(d) : new Date();
    x.setHours(0, 0, 0, 0);
    return x;
}

function addDays(d, offset) {
    const x = new Date(d);
    x.setDate(x.getDate() + offset);
    return startOfDay(x);
}

function formatDayShort(date) {
    return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
}

function formatDateNum(date) {
    return new Intl.DateTimeFormat('en-US', { day: '2-digit' }).format(date);
}

export default function ProductDates({ productId = '', isLoading = false, isDisabled = false, defaultDate = null, availability = [], onSelectDate = (d) => {}, initialStartDate = new Date() }) {
    const [selectedDate, setSelectedDate] = useState(defaultDate ? formattedDate3(startOfDay(defaultDate)) : null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { handleGetProductDetails, selectedDetailDate: date } = useOnlineSales();

    const [startDate, setStartDate] = useState(() => startOfDay(initialStartDate));

    useEffect(() => {
        const newStart = startOfDay(initialStartDate);
        setStartDate((prevStartDate) => {
            const currentStart = prevStartDate.getTime();
            const currentEnd = addDays(prevStartDate, 6).getTime();
            const newTime = newStart.getTime();

            if (newTime >= currentStart && newTime <= currentEnd) {
                return prevStartDate;
            }
            return newStart;
        });
    }, [initialStartDate]);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const availabilityMap = useMemo(() => {
        const map = new Map();
        for (const item of availability) {
            if (!item || !item.date) continue;
            const d = startOfDay(new Date(item.date));
            const key = formattedDate3(d);
            map.set(key, item.availability);
        }
        return map;
    }, [availability]);

    const week = useMemo(() => {
        const arr = [];
        for (let i = 0; i < 7; i++) {
            const d = addDays(startDate, i);

            const key = formattedDate3(d);
            arr.push({
                date: d,
                availability: availabilityMap.has(key) ? availabilityMap.get(key) : 'available',
            });
        }
        return arr;
    }, [startDate, availabilityMap]);

    const unavailableDates = useMemo(() => availability.filter((item) => item.availability !== 'available').map((item) => new Date(item.date)), [availability]);

    const isUnavailable = (date) => {
        return unavailableDates.some((unavailable) => date.getFullYear() === unavailable.getFullYear() && date.getMonth() === unavailable.getMonth() && date.getDate() === unavailable.getDate());
    };

    useEffect(() => {
        if (date) {
            const startDate = startOfDay(date);
            const key = formattedDate3(startDate);
            if (key !== selectedDate) {
                setSelectedDate(key);
            }
        }
    }, [date, selectedDate]);

    const handleSelect = (date) => {
        const startDate = startOfDay(date);
        const key = formattedDate3(startDate);

        if (key === selectedDate) return;

        const status = availabilityMap.has(key) ? availabilityMap.get(key) : 'available';

        if (status !== 'available') return;

        setSelectedDate(key);
        onSelectDate(startDate);

        if (productId && productId !== '') {
            handleGetProductDetails(productId, key, 120);
        }
    };

    if (isDisabled) {
        return null;
    }

    return (
        <Box w='full'>
            <Box w='full' mb={5}>
                <Flex gap={2} alignItems='center'>
                    <Heading fontSize={{ base: 'xl', md: 'xl' }} fontWeight={500}>
                        {isDisabled ? 'Current Date' : 'Select Date'}
                    </Heading>
                    <Text fontSize='sm' color='red.400' fontWeight={700} fontStyle='italic'>
                        *Required
                    </Text>
                </Flex>

                {isDisabled && (
                    <Text color='red' fontSize='14px' fontStyle='italic' mt={2}>
                        *You can only select a booking date once.
                    </Text>
                )}
            </Box>

            <Grid w='full' h='auto' gridTemplateColumns={{ base: 'repeat(4, 1fr)', md: 'repeat(8, 1fr)' }} gap={2}>
                {week.map((item) => {
                    const date = startOfDay(item.date);
                    const month = date.getMonth() + 1;
                    const key = formattedDate3(date);
                    const isSelected = selectedDate === key;
                    const isNotAvailable = item.availability !== 'available' || (isDisabled && !isSelected);

                    if (isDisabled && isNotAvailable) {
                        return null;
                    }

                    return (
                        <GridItem key={key} w='full' h='90px' cursor={isNotAvailable || isDisabled ? 'not-allowed' : 'pointer'} transition='all 0.25s ease-in-out' role='group'>
                            <Skeleton isLoaded={!isLoading} rounded='xl' h='full' w='full'>
                                <Box
                                    w='full'
                                    h='full'
                                    opacity={isNotAvailable ? 0.35 : 1}
                                    bg={isSelected ? 'primary.500' : 'transparent'}
                                    border='1px'
                                    borderColor='gray.200'
                                    rounded='lg'
                                    onClick={
                                        isDisabled
                                            ? null
                                            : () => {
                                                  if (isNotAvailable) return;

                                                  handleSelect(date);
                                              }
                                    }
                                    _hover={isNotAvailable || isDisabled ? {} : { bg: 'primary.100' }}>
                                    <Center w='full' h='full' flexDirection='column'>
                                        <Text color={isSelected ? 'white' : 'black'} textAlign='center' fontSize={{ base: 'lg', md: '2xl' }} fontWeight={600}>
                                            {formatDateNum(date)}
                                        </Text>
                                        <Text color={isSelected ? 'white' : 'gray'} textAlign='center' fontSize={isNotAvailable ? 'xs' : 'sm'} mt={1} lineHeight={1.2}>
                                            {isNotAvailable ? 'Not Available' : `${formatDayShort(date)}`}
                                        </Text>
                                        <Text color={isSelected ? 'white' : 'gray'} textAlign='center' fontSize={'xs'} mt={1} lineHeight={1.2}>
                                            {`( ${months[month - 1]} )`}
                                        </Text>
                                    </Center>
                                </Box>
                            </Skeleton>
                        </GridItem>
                    );
                })}

                {isDisabled || (
                    <GridItem w='full' h='90px' bg={isDisabled ? 'gray.300' : 'primary.500'} cursor={isDisabled ? 'not-allowed' : 'pointer'} border='1px' borderColor='gray.200' rounded='lg' onClick={isDisabled ? null : onOpen}>
                        <Center w='full' h='full' flexDirection='column'>
                            <Icon as={LuCalendarClock} fontSize='20px' color='white' />
                            <Text mt={1} fontSize='sm' fontWeight={500} color='white'>
                                More
                            </Text>
                        </Center>
                    </GridItem>
                )}
            </Grid>

            {isOpen && (
                <Portal>
                    <Box position='fixed' top={0} left={0} right={0} bottom={0} display='flex' alignItems='center' justifyContent='center' bg='rgba(0,0,0,0.4)' zIndex={9999} onClick={onClose}>
                        <Container maxW='3xl'>
                            <Box bg='white' p={4} borderRadius='md' onClick={(e) => e.stopPropagation()}>
                                <Calendar
                                    calendarType='hebrew'
                                    locale='en'
                                    minDate={new Date()}
                                    maxDate={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)}
                                    className='react-calendar-large'
                                    onChange={(date) => {
                                        const chosen = Array.isArray(date) ? date[0] : date;
                                        handleSelect(chosen);
                                        onClose();
                                    }}
                                    value={selectedDate ? selectedDate : startDate}
                                    tileDisabled={({ date, view }) => {
                                        if (view !== 'month') return false;
                                        return unavailableDates.some((unavailable) => date.getFullYear() === unavailable.getFullYear() && date.getMonth() === unavailable.getMonth() && date.getDate() === unavailable.getDate());
                                    }}
                                    tileContent={({ date, view }) =>
                                        isUnavailable(date) ? (
                                            <Text fontSize='xs' color='red.500' mt={1} noOfLines={{ base: 1, md: 2 }}>
                                                Not Available
                                            </Text>
                                        ) : null
                                    }
                                />
                            </Box>
                        </Container>
                    </Box>
                </Portal>
            )}
        </Box>
    );
}
